'use client';

/**
 * DynamicForm Component
 *
 * A form generator that dynamically renders fields from FieldDefinition arrays.
 * Supports grouping, conditional visibility, validation, and react-hook-form integration.
 */

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useI18n } from '../../hooks/useI18n';
import type { NeonAccent } from '../../theme/palette';
import { baseColors } from '../../theme/palette';
import { transitionPresets } from '../../theme/transitions';
import type { FieldDefinition, FormSection, VisibilityCondition } from '../../types/entityFramework';
import { isFormValid, validateFields } from '../../utils/validators';
import { FieldRenderer } from '../FieldRenderer';
import { GlassButton } from '../GlassButton';
import { GlassCard } from '../GlassCard';

// ============================================================================
// Types
// ============================================================================

export interface DynamicFormProps {
  /** Array of field definitions to render */
  fields: FieldDefinition[];
  /** Initial form values */
  values?: Record<string, unknown>;
  /** Submit handler */
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /** Optional change handler called on every field change */
  onChange?: (values: Record<string, unknown>) => void;
  /** Cancel handler */
  onCancel?: () => void;
  /** Disable all form fields */
  disabled?: boolean;
  /** Show loading state on submit button */
  loading?: boolean;
  /** Submit button label */
  submitLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Neon accent color for styling */
  neonAccent?: NeonAccent;
  /** Form variant */
  variant?: 'glass' | 'flat';
  /** Show field labels */
  showLabels?: boolean;
  /** Number of columns for field layout */
  columns?: 1 | 2 | 3;
  /** Additional CSS class */
  className?: string;
  /** Form sections for grouping fields */
  sections?: FormSection[];
  /** Whether sections are collapsible */
  collapsibleSections?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledFormContainerProps {
  $variant: 'glass' | 'flat';
}

const StyledFormContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledFormContainerProps>(({ $variant }) => ({
  width: '100%',
  ...($variant === 'flat' && {
    padding: 0,
  }),
}));

const SectionContainer = styled(motion.div)({
  marginBottom: 24,
  '&:last-child': {
    marginBottom: 0,
  },
});

interface SectionHeaderProps {
  $isDark: boolean;
  $isCollapsible: boolean;
  $neonAccent?: NeonAccent;
}

const SectionHeaderStyled = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<SectionHeaderProps>(({ $isDark, $isCollapsible, $neonAccent }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 16,
  cursor: $isCollapsible ? 'pointer' : 'default',
  padding: '8px 0',
  borderBottom: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
  transition: transitionPresets.allFast,
  '&:hover': {
    borderBottomColor: $neonAccent
      ? alpha(baseColors.primary[500], 0.3)
      : $isDark
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.15)',
  },
}));

const FieldContainer = styled(motion.div)({
  width: '100%',
});

const ActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
}));

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Evaluate a visibility condition against form values
 */
function evaluateVisibilityCondition(condition: VisibilityCondition, values: Record<string, unknown>): boolean {
  const fieldValue = values[condition.field];

  switch (condition.operator) {
    case 'equals':
      return fieldValue === condition.value;
    case 'notEquals':
      return fieldValue !== condition.value;
    case 'contains':
      if (typeof fieldValue === 'string' && typeof condition.value === 'string') {
        return fieldValue.includes(condition.value);
      }
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(condition.value);
      }
      return false;
    case 'isEmpty':
      return (
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );
    case 'isNotEmpty':
      return !(
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === '' ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      );
    case 'greaterThan':
      if (typeof fieldValue === 'number' && typeof condition.value === 'number') {
        return fieldValue > condition.value;
      }
      return false;
    case 'lessThan':
      if (typeof fieldValue === 'number' && typeof condition.value === 'number') {
        return fieldValue < condition.value;
      }
      return false;
    default:
      return true;
  }
}

/**
 * Check if a field should be visible based on its visibility condition
 */
function isFieldVisible(field: FieldDefinition, values: Record<string, unknown>): boolean {
  if (!field.visibilityCondition) {
    return true;
  }
  return evaluateVisibilityCondition(field.visibilityCondition, values);
}

/**
 * Group fields by their section property
 */
function groupFieldsBySection(
  fields: FieldDefinition[],
  sections?: FormSection[],
): Map<string | null, FieldDefinition[]> {
  const groups = new Map<string | null, FieldDefinition[]>();

  if (sections && sections.length > 0) {
    // Initialize groups for each section
    for (const section of sections) {
      groups.set(section.id, []);
    }
    groups.set(null, []); // For fields without a section

    // Assign fields to sections
    for (const field of fields) {
      const sectionId = field.section ?? null;
      const group = groups.get(sectionId) ?? [];
      group.push(field);
      groups.set(sectionId, group);
    }
  } else {
    // Group by field.section property
    for (const field of fields) {
      const sectionId = field.section ?? null;
      const group = groups.get(sectionId) ?? [];
      group.push(field);
      groups.set(sectionId, group);
    }
  }

  return groups;
}

// ============================================================================
// Chevron Icon Component
// ============================================================================

interface ChevronIconProps {
  expanded: boolean;
  isDark: boolean;
}

const ChevronIcon: React.FC<ChevronIconProps> = ({ expanded, isDark }) => (
  <motion.svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    animate={{ rotate: expanded ? 180 : 0 }}
    transition={{ duration: 0.2 }}
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke={isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </motion.svg>
);

// ============================================================================
// Component
// ============================================================================

const EMPTY_VALUES: Record<string, unknown> = {};

export const DynamicForm: React.FC<DynamicFormProps> = ({
  fields,
  values: propValues,
  onSubmit,
  onChange,
  onCancel,
  disabled = false,
  loading = false,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  neonAccent,
  variant = 'glass',
  showLabels: _showLabels = true,
  columns = 1,
  className,
  sections,
  collapsibleSections = true,
}) => {
  // Note: _showLabels is passed to FieldRenderer which handles label display internally
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  // Stabilize initialValues reference to prevent infinite re-render loop.
  // Using `= {}` as a default parameter creates a new object on every render,
  // which triggers the reset useEffect, which triggers watch() re-render, ad infinitum.
  const initialValues = propValues ?? EMPTY_VALUES;

  // Track collapsed sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    const collapsed = new Set<string>();
    if (sections) {
      for (const section of sections) {
        if (section.defaultCollapsed) {
          collapsed.add(section.id);
        }
      }
    }
    return collapsed;
  });

  // Initialize react-hook-form
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    watch,
    formState: { errors: rhfErrors },
    reset,
  } = useForm({
    defaultValues: initialValues as Record<string, unknown>,
    mode: 'onBlur',
  });

  // Watch all form values for conditional visibility and onChange callback
  const watchedValues = watch();

  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Reset form when initialValues change
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Call onChange when values change
  useEffect(() => {
    onChange?.(watchedValues);
  }, [watchedValues, onChange]);

  // Group fields by section
  const groupedFields = useMemo(() => groupFieldsBySection(fields, sections), [fields, sections]);

  // Get section info
  const getSectionInfo = useCallback(
    (sectionId: string | null): FormSection | undefined => {
      if (!sectionId || !sections) return undefined;
      return sections.find((s) => s.id === sectionId);
    },
    [sections],
  );

  // Toggle section collapse
  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Handle form submission with validation
  const handleFormSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      // Get visible fields only for validation
      const visibleFields = fields.filter((field) => isFieldVisible(field, data));

      // Run validation
      const validationResults = validateFields(data, visibleFields);
      const newErrors: Record<string, string> = {};

      for (const [fieldName, result] of Object.entries(validationResults)) {
        if (!result.isValid && result.errors.length > 0) {
          newErrors[fieldName] = result.errors[0];
        }
      }

      setValidationErrors(newErrors);

      if (!isFormValid(validationResults)) {
        return;
      }

      // Submit the form
      await onSubmit(data);
    },
    [fields, onSubmit],
  );

  // Get error message for a field
  const getFieldError = useCallback(
    (fieldName: string): string | undefined => {
      // Check react-hook-form errors first
      const rhfError = rhfErrors[fieldName];
      if (rhfError?.message) {
        return rhfError.message as string;
      }
      // Fall back to custom validation errors
      return validationErrors[fieldName];
    },
    [rhfErrors, validationErrors],
  );

  // Calculate grid columns
  const getGridColumns = useCallback(
    (fieldWidth?: number) => {
      const baseColumns = 12 / columns;
      if (fieldWidth) {
        return Math.min(fieldWidth, 12);
      }
      return baseColumns;
    },
    [columns],
  );

  // Render a single field
  const renderField = useCallback(
    (field: FieldDefinition) => {
      // Check visibility
      if (!isFieldVisible(field, watchedValues)) {
        return null;
      }

      const error = getFieldError(field.name);
      const gridColumns = getGridColumns(field.width);

      return (
        <Grid size={{ xs: 12, sm: gridColumns }} key={field.name}>
          <AnimatePresence mode="wait">
            <FieldContainer
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Controller
                name={field.name}
                control={control}
                rules={{
                  required: field.required ? `${field.label} is required` : false,
                }}
                render={({ field: controllerField }) => (
                  <FieldRenderer
                    field={field}
                    value={controllerField.value}
                    onChange={(value) => {
                      controllerField.onChange(value);
                      // Clear validation error when field changes
                      if (validationErrors[field.name]) {
                        setValidationErrors((prev) => {
                          const next = { ...prev };
                          delete next[field.name];
                          return next;
                        });
                      }
                    }}
                    mode="edit"
                    error={error}
                    disabled={disabled || field.disabled || loading}
                    neonAccent={neonAccent}
                    size="medium"
                  />
                )}
              />
            </FieldContainer>
          </AnimatePresence>
        </Grid>
      );
    },
    [watchedValues, getFieldError, getGridColumns, control, disabled, loading, neonAccent, validationErrors],
  );

  // Render a section
  const renderSection = useCallback(
    (sectionId: string | null, sectionFields: FieldDefinition[]) => {
      const sectionInfo = getSectionInfo(sectionId);
      const isCollapsed = sectionId ? collapsedSections.has(sectionId) : false;
      const isCollapsible = collapsibleSections && sectionInfo?.collapsible !== false && sectionId;

      // Filter visible fields
      const visibleFields = sectionFields.filter((field) => isFieldVisible(field, watchedValues));

      // Don't render empty sections
      if (visibleFields.length === 0) {
        return null;
      }

      const sectionContent = (
        <Grid container spacing={2}>
          {visibleFields.map(renderField)}
        </Grid>
      );

      // If no section info, render fields directly
      if (!sectionInfo) {
        return <SectionContainer key="ungrouped">{sectionContent}</SectionContainer>;
      }

      // Render section with header
      return (
        <SectionContainer key={sectionInfo.id}>
          <SectionHeaderStyled
            $isDark={isDark}
            $isCollapsible={!!isCollapsible}
            $neonAccent={neonAccent}
            onClick={isCollapsible ? () => toggleSection(sectionInfo.id) : undefined}
          >
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {sectionInfo.title}
              </Typography>
              {sectionInfo.description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                    mt: 0.5,
                  }}
                >
                  {sectionInfo.description}
                </Typography>
              )}
            </Box>
            {isCollapsible && (
              <IconButton size="small" sx={{ p: 0.5 }}>
                <ChevronIcon expanded={!isCollapsed} isDark={isDark} />
              </IconButton>
            )}
          </SectionHeaderStyled>
          <Collapse in={!isCollapsed} timeout={200}>
            {sectionContent}
          </Collapse>
        </SectionContainer>
      );
    },
    [
      getSectionInfo,
      collapsedSections,
      collapsibleSections,
      watchedValues,
      renderField,
      isDark,
      neonAccent,
      toggleSection,
    ],
  );

  // Form content
  const formContent = (
    <>
      {Array.from(groupedFields.entries()).map(([sectionId, sectionFields]) => renderSection(sectionId, sectionFields))}

      <ActionContainer>
        {onCancel && (
          <GlassButton variant="ghost" color="secondary" onClick={onCancel} disabled={disabled || loading}>
            {cancelLabel}
          </GlassButton>
        )}
        <GlassButton
          variant="gradient"
          type="submit"
          disabled={disabled}
          loading={loading}
          loadingText={t('common.submitting')}
          color="primary"
        >
          {submitLabel}
        </GlassButton>
      </ActionContainer>
    </>
  );

  return (
    <form onSubmit={rhfHandleSubmit(handleFormSubmit)} className={className}>
      <StyledFormContainer $variant={variant}>
        {variant === 'glass' ? (
          <GlassCard neonAccent={neonAccent} glowOnHover={false} hoverLift={false}>
            {formContent}
          </GlassCard>
        ) : (
          formContent
        )}
      </StyledFormContainer>
    </form>
  );
};

DynamicForm.displayName = 'DynamicForm';

export default DynamicForm;
